import React, { useState, useMemo } from 'react';
import { Modal, Form, InputGroup, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import LinkProduto from "components/common/LinkProduto";
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { usePesquisarProdutosInfinito, useIncluirProduto } from '../../../../hooks/useBalanco';
import { ProdutoParaBalancoType } from '../../../../services/balancoService';
import { convertDateToUTC } from '../../../../utils/dateUtils';

interface ModalPesquisarProdutosProps {
    show: boolean;
    onHide: () => void;
}

const ModalPesquisarProdutos: React.FC<ModalPesquisarProdutosProps> = ({
    show,
    onHide
}) => {
    const [termoPesquisa, setTermoPesquisa] = useState('');
    const [produtoSelecionado, setProdutoSelecionado] = useState<ProdutoParaBalancoType | null>(null);
    const [showModalControlado, setShowModalControlado] = useState(false);
    const [quantidade, setQuantidade] = useState(1);
    const [lote, setLote] = useState('');
    const [validade, setValidade] = useState('');

    // Estado para controlar se o modal principal deve estar temporariamente oculto
    const [modalPrincipalTemporariamenteOculto, setModalPrincipalTemporariamenteOculto] = useState(false);

    // Query de pesquisa (só executa quando modal está aberto e há termo de pesquisa)
    const produtosQuery = usePesquisarProdutosInfinito(show && termoPesquisa ? termoPesquisa : undefined);

    // Mutation para incluir produto
    const incluirProdutoMutation = useIncluirProduto();

    // Preparar dados para rolagem infinita
    const allProdutos = useMemo(() => {
        if (!produtosQuery.data) return [];
        return produtosQuery.data.pages.flatMap(page =>
            page.isSuccess ? page.data : []
        );
    }, [produtosQuery.data]);

    // Verificar se há mais itens para carregar
    const hasNextPage = produtosQuery.hasNextPage;
    const isLoadingMore = produtosQuery.isFetchingNextPage;

    const handlePesquisar = () => {
        // A query será refeita automaticamente devido ao useInfiniteQuery
        produtosQuery.refetch();
    };

    const handleSelecionarProduto = async (produtoId: number) => {
        const produto = allProdutos.find((p: ProdutoParaBalancoType) => p.id === produtoId);
        if (produto) {
            if (produto.controleEspecial) {
                // Produto controlado - abrir modal para informar lote/validade
                setProdutoSelecionado(produto);
                setModalPrincipalTemporariamenteOculto(true); // Ocultar modal principal temporariamente
                setShowModalControlado(true);
            } else {
                // Produto normal - incluir diretamente
                try {
                    await incluirProdutoMutation.mutateAsync({
                        produtoId: produto.id,
                        quantidade: 1,
                    });
                    // Fechar modal após inclusão bem-sucedida
                    onHide();
                } catch (error) {
                    // Erro será tratado pelo hook
                }
            }
        }
    };

    // Função para incluir produto controlado (com lote/validade)
    const incluirProdutoControlado = async () => {
        if (!produtoSelecionado) return;

        try {
            await incluirProdutoMutation.mutateAsync({
                produtoId: produtoSelecionado.id,
                quantidade,
                lote: lote || undefined,
                validade: convertDateToUTC(validade),
            });

            // Limpar estados e fechar modais
            setShowModalControlado(false);
            setModalPrincipalTemporariamenteOculto(false);
            setProdutoSelecionado(null);
            setQuantidade(1);
            setLote('');
            setValidade('');
            onHide();
        } catch (error) {
            // Erro será tratado pelo hook
        }
    };

    // Função para verificar se um item está carregado
    const isItemLoaded = (index: number) => !!allProdutos[index];

    // Função para carregar mais itens
    const loadMoreItems = () => {
        if (hasNextPage && !isLoadingMore) {
            produtosQuery.fetchNextPage();
        }
    };

    return (
        <>
            <Modal show={show && !modalPrincipalTemporariamenteOculto} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faSearch} className="me-2 text-primary-emphasis" />
                        Pesquisar e Incluir Produtos
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Barra de pesquisa */}
                    <Row className="mb-4">
                        <Col>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Pesquisar por nome, código de barras ou fabricante..."
                                    value={termoPesquisa}
                                    onChange={(e) => setTermoPesquisa(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                                />
                                <Button variant="outline-secondary" onClick={handlePesquisar}>
                                    <FontAwesomeIcon icon={faSearch} />
                                </Button>
                                {termoPesquisa && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setTermoPesquisa('')}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                    </Row>

                    {/* Lista de produtos com rolagem infinita */}
                    {termoPesquisa ? (
                        <div>
                            {/* Header da tabela */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <strong>{allProdutos.length}</strong> produto(s) carregado(s)
                                </div>
                            </div>

                            {/* Lista virtualizada */}
                            <InfiniteLoader
                                isItemLoaded={isItemLoaded}
                                itemCount={hasNextPage ? allProdutos.length + 1 : allProdutos.length}
                                loadMoreItems={loadMoreItems}
                            >
                                {({ onItemsRendered, ref }) => (
                                    <List
                                        ref={ref}
                                        height={400}
                                        itemCount={hasNextPage ? allProdutos.length + 1 : allProdutos.length}
                                        itemSize={80}
                                        onItemsRendered={onItemsRendered}
                                        width="100%"
                                        itemData={{
                                            produtos: allProdutos,
                                            handleSelecionarProduto,
                                            isLoadingMore,
                                            hasNextPage
                                        }}
                                    >
                                        {ProdutoRow}
                                    </List>
                                )}
                            </InfiniteLoader>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSearch} size="3x" className="text-body-secondary mb-3" />
                            <h5 className="text-body-secondary mb-3">Pesquisar Produtos</h5>
                            <p className="text-body-secondary">
                                Digite o nome, código de barras ou fabricante do produto que deseja incluir no balanço.
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para produtos controlados */}
            {produtoSelecionado && (
                <Modal
                    show={showModalControlado}
                    onHide={() => {
                        setShowModalControlado(false);
                        setModalPrincipalTemporariamenteOculto(false); // Reabrir modal principal
                        setProdutoSelecionado(null);
                        setQuantidade(1);
                        setLote('');
                        setValidade('');
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Produto Controlado
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <strong>{produtoSelecionado.nome}</strong>
                            <div className="small text-body-secondary">
                                Código: {produtoSelecionado.codigo} | Estoque Atual: {produtoSelecionado.estoqueAtual}
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Quantidade Contada *</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={quantidade}
                                onChange={(e) => setQuantidade(parseInt(e.target.value) || 0)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Lote *</Form.Label>
                            <Form.Control
                                type="text"
                                value={lote}
                                onChange={(e) => setLote(e.target.value.toUpperCase())}
                                placeholder="Digite o lote"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Validade *</Form.Label>
                            <Form.Control
                                type="date"
                                value={validade}
                                onChange={(e) => setValidade(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowModalControlado(false);
                                setModalPrincipalTemporariamenteOculto(false); // Reabrir modal principal
                                setProdutoSelecionado(null);
                                setQuantidade(1);
                                setLote('');
                                setValidade('');
                            }}
                            disabled={incluirProdutoMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={incluirProdutoControlado}
                            disabled={!lote.trim() || !validade || quantidade <= 0 || incluirProdutoMutation.isPending}
                        >
                            {incluirProdutoMutation.isPending ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                    </div>
                                    Incluindo...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Incluir no Balanço
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

// Componente para renderizar cada produto na lista
const ProdutoRow = ({ index, style, data }: any) => {
    const { produtos, handleSelecionarProduto, isLoadingMore, hasNextPage } = data;

    // Se estamos no final da lista e ainda há mais páginas, mostrar loading
    if (index >= produtos.length) {
        return (
            <div style={style} className="d-flex align-items-center justify-content-center">
                {isLoadingMore ? (
                    <div className="text-body-secondary">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        Carregando mais produtos...
                    </div>
                ) : hasNextPage ? (
                    <div className="text-body-secondary">Carregando...</div>
                ) : null}
            </div>
        );
    }

    const produto = produtos[index];

    return (
        <div style={style} className="border-bottom p-2">
            <div className="d-flex align-items-center">
                <div className="me-3">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleSelecionarProduto(produto.id)}
                    >
                        {produto.jaNoBalanco ? "Adicionar" : "Incluir"}
                    </Button>
                </div>
                <div className="flex-grow-1">
                    <div>
                        <LinkProduto produtoId={produto.id}>
                            <strong>{produto.nome}</strong>
                        </LinkProduto>
                        <div className="text-body-secondary small">
                            Código: {produto.codigo}
                            {produto.codigoBarras && ` | EAN: ${produto.codigoBarras}`}
                        </div>
                        <div className="text-body-secondary small">
                            {produto.fabricante || 'Sem fabricante'}
                        </div>
                    </div>
                </div>
                <div className="text-end">
                    <div className={produto.estoqueAtual > 0 ? 'text-success-emphasis' : 'text-body-secondary'}>
                        {produto.estoqueAtual} {produto.unidade || 'UN'}
                    </div>
                    <div className="d-flex gap-1 justify-content-end mt-1">
                        {produto.controleEspecial && (
                            <span className="badge bg-warning text-dark">Controlado</span>
                        )}
                        {produto.jaNoBalanco ? (
                            <span className="badge bg-info">Já no Balanço</span>
                        ) : (
                            <span className="badge bg-secondary">Disponível</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalPesquisarProdutos; 